/*
 * Do not use directly. This component is used by the Image component to build the actual
 * img tag and its attributes. It can handle fixed or responsive images.
 */
import startCase from 'lodash/startCase'

import { lcpImageSizing } from '@/lib/images/lcp-optimizer'
import { default as initTracer } from '@/lib/utils/tracer'

import { ImageLoadState } from './client-image'
import { ImageProps, ImgProps } from './types'

const buildSrc = (props: ImageProps): string => {
  const { image, sizingProps } = props
  const { toUrl, nativeWidth = 1366, nativeHeight = 768, quality, trim } = sizingProps || {}
  const src = image as string
  if (toUrl) {
    return toUrl({ baseUrl: src, width: nativeWidth, height: nativeHeight, quality, trim })
  }
  return src
}

const buildSrcSets = (props: ImageProps): string[] => {
  const { image, sizingProps: sz, tracer: t } = props
  const src = image as string
  const sizingProps = sz!
  const tracer = t!
  const { resolutionCount = 5, toUrl, quality, trim } = sizingProps
  tracer!.trace('buildSrcSets: %o', { src, sizingProps })

  const maxWidth = Math.min(sizingProps.maxWidth || 1366, sizingProps.nativeWidth || Infinity)
  if (sizingProps.widths?.length && toUrl) {
    const widths = [...new Set([...sizingProps.widths.filter((width) => width > 0 && width < maxWidth), maxWidth])]
    return widths.sort((a, b) => a - b).map((width) => `${toUrl({ baseUrl: src, width, quality, trim })} ${width}w`)
  }
  if (sizingProps.minWidth && maxWidth && sizingProps.minWidth > maxWidth) {
    tracer.trace('Image has invalid dimensions')
  }
  const minWidth =
    (maxWidth && sizingProps.minWidth && sizingProps.minWidth > maxWidth ? maxWidth : sizingProps.minWidth) || 300

  const srcSets = []

  if (!toUrl) {
    tracer.trace('No toUrl so just returning one src set')
    srcSets.push(`${src}`)
    return srcSets
  }

  // Always include minWidth
  srcSets.push(`${toUrl({ baseUrl: src, width: minWidth, quality, trim })} ${minWidth}w`)
  if (resolutionCount < 2) {
    tracer.trace('%o: rc < 2', src)
    // Only one was requested
    return srcSets
  }
  if (/svg|data/.test(src)) {
    tracer.trace('%o: svg/data', src.slice(0, 20))
    // only one res for svg/base64
    return srcSets
  }
  const diff = maxWidth - minWidth
  if (diff === 0) {
    tracer.trace('%o: diff === 0', src)
    // just one needed
    return srcSets
  }
  if (resolutionCount === 2) {
    tracer.trace('rc === 2 || diff < 2: %o', { src, resolutionCount, diff })
    // just min and max needed
    srcSets.push(`${toUrl({ baseUrl: src, width: maxWidth, quality, trim })} ${maxWidth}w`)
    return srcSets
  }
  // 200px is about what we're shooting for so default of 300 - 1366 with 5 resolutions
  // gets us that, if the difference is lower than 200, just return the min and max.
  const pixelWidth = Math.max(200, Math.floor((maxWidth - minWidth) / (resolutionCount - 1)))
  let currentWidth = minWidth + pixelWidth
  tracer.trace('width: %o', { src, resolutionCount, diff, pixelWidth, currentWidth })
  while (currentWidth < maxWidth) {
    srcSets.push(`${toUrl({ baseUrl: src, width: currentWidth, quality, trim })} ${currentWidth}w`)
    currentWidth += pixelWidth
  }
  // Final one for maxWidth
  srcSets.push(`${toUrl({ baseUrl: src, width: maxWidth, quality, trim })} ${maxWidth}w`)
  return srcSets
}

// Fixed-size images use pixel density srcset, e.g. url 1x, url 2x
const fixed = (props: ImageProps): ImgProps => {
  const { image, sizingProps, imgProps } = props
  const src = image as string
  const {
    fixedWidth,
    fixedHeight,
    nativeWidth,
    nativeHeight,
    resolutionCount = 2,
    aspectRatio,
    toUrl,
    quality,
    trim,
  } = sizingProps!

  if (!fixedWidth) {
    console.warn('[optimized-image] Fixed image specified but no fixedWidth supplied: %s', image)
  }

  const width = fixedWidth || nativeWidth || 300
  let height = fixedHeight || nativeHeight
  if ((!height || height === 'auto') && aspectRatio) {
    height = Math.floor(width / aspectRatio)
  }
  if (!height || (height !== 'auto' && isNaN(height))) {
    height = 'auto'
  }

  const srcs = []
  if (toUrl) {
    srcs.push(toUrl({ baseUrl: src!, width, height, quality, trim }))
    for (let i = 1; i < resolutionCount; ++i) {
      srcs.push(
        toUrl({
          baseUrl: src!,
          width: width * i * 2,
          height: height === 'auto' ? height : height * i * 2,
          quality,
          trim,
        })
      )
    }
  } else {
    srcs.push(src)
  }

  const srcSet = srcs.length === 1 ? undefined : srcs.map((s, i) => `${s} ${i + 1}x`).join(',')
  return {
    width: nativeWidth || width, // This needs to be native width ideally so the browser doesn't have to scale the image initially; I think
    height: nativeHeight || height,
    src: buildSrc(props),
    srcSet,
    style: {
      width: `${width}px`,
      height: `${height}px`,
      maxWidth: '100%',
      aspectRatio: aspectRatio || undefined,
      ...imgProps?.style,
    },
  }
}

// Responsive images use media query srcset, e.g. url 100w, url 200w,...
const responsive = (props: ImageProps): ImgProps => {
  const { image, tracer, imgProps, sizingProps } = props
  if (typeof image === 'string' && /data.*base64/.test(image as string)) {
    return {
      src: image as string,
    }
  }
  const srcSets = buildSrcSets(props)
  const { nativeWidth, nativeHeight, aspectRatio, minWidth, sizes = '100vw' } = sizingProps!

  tracer!.trace('srcsets for responsive: %o', srcSets)
  const src = buildSrc(props)
  return {
    src,
    width: nativeWidth || minWidth,
    height: nativeHeight || 'auto',
    srcSet: srcSets.join(','),
    sizes,
    style: { maxWidth: '100%', aspectRatio: aspectRatio || undefined, ...imgProps?.style },
  }
}

const sizingAttributes = (props: ImageProps): ImgProps => {
  if (props.sizingProps?.fixed) {
    return fixed(props)
  }
  return responsive(props)
}

export const PreloadLink = (props: ImageProps) => {
  // Use the image's sizing mode: fixed images have density descriptors (1x,
  // 2x). Rebuilding a responsive srcset here downloads a second, unused image.
  const { src, srcSet, sizes = props.imgProps?.sizes ?? '100vw' } = sizingAttributes(props)
  return (
    <link
      rel="preload"
      as="image"
      href={srcSet || typeof src !== 'string' ? undefined : src}
      imageSrcSet={srcSet}
      imageSizes={sizes}
      media={props.preloadMedia}
      fetchPriority={props.preloadMedia ? 'high' : undefined}
    />
  )
}

const defaultImgProps: ImgProps = {
  sizes: '100vw',
}

const OptimizedImage = (props: ImageProps) => {
  const deliverySizing =
    props.delivery === 'same-origin' && typeof props.image === 'string'
      ? lcpImageSizing(props.image, props.sizingProps)
      : undefined
  if (deliverySizing) {
    props = { ...props, sizingProps: deliverySizing }
  }
  const { image, sizingProps, imgProps, loadTime, origin, tracer = initTracer(), blockRobots, placeHolder } = props

  try {
    tracer.trace('Optimizing image: %o', props)
    const src = image as string

    // Overwrite imgprops loading with ours since ours is required
    // Note that ours can be eager, lazy, idle, or nav
    const loading: 'eager' | 'lazy' = loadTime === 'eager' ? 'eager' : 'lazy'

    // Note: supposedly Firefox ignores the loading attribute unless it's before src
    const allProps = {
      ...defaultImgProps,
      ...imgProps,
      'data-origin': origin,
      loading,
      'data-load-time': loadTime,
      src,
    }
    if (sizingProps) {
      Object.assign(allProps, { ...sizingAttributes(props) })
    }

    if (!allProps.alt && allProps.src) {
      try {
        allProps.alt = startCase(allProps.src.split('/').slice(-1)[0].split('.')[0])
      } catch (ex) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[optimized-image] could not parse %s for default alt: %s', src, (ex as Error).message)
        }
      }
    }
    if (blockRobots) {
      allProps.src = `${allProps.src}${allProps.src.includes('?') ? '&robots=block' : '?robots=block'}`
    }
    // Set high fetch priority for non-lazy images (eager or no loading attribute)
    if (allProps.loading !== 'lazy' && !allProps.fetchPriority) {
      allProps.fetchPriority = 'high'
    }
    if (process.env.NODE_ENV !== 'production') {
      // Object.assign(allProps, { 'data-attributes': JSON.stringify({ ...allProps, src: allProps.src.slice(0, 50) }) });
    }
    const imageElement =
      placeHolder && loadTime !== 'eager' ? (
        <ImageLoadState placeHolder={placeHolder}>
          <img {...allProps} />
        </ImageLoadState>
      ) : (
        <img {...allProps} />
      )

    if (loadTime === 'eager' || props.preloadMedia) {
      return (
        <>
          {sizingProps ? <PreloadLink {...props} tracer={tracer} /> : null}
          {imageElement}
        </>
      )
    }

    return imageElement
  } catch (ex: unknown) {
    console.error('[optimized-image]', ex)
    if ((ex as Error).message !== undefined) {
      tracer.trace('Exception in optimized image: %s', (ex as Error).message)
    }
    return null
  } finally {
    // console.debug(tracer.dump())
  }
}

export default OptimizedImage
